import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Input } from './ui/input';
import { Label } from './ui/label';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. The link may be expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden"
        data-testid="reset-password-page"
      >
        <div className="bg-gradient-to-br from-[#FEF3C7] via-[#FFEDD5] to-[#FFE4E6] py-6 px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Sparkles className="w-6 h-6 text-[#FD5E53]" />
            <span className="font-fredoka text-2xl text-slate-800">Craven-it</span>
          </div>
          <h1 className="font-fredoka text-xl text-slate-800">Set a new password</h1>
          <p className="font-nunito text-sm text-slate-600 mt-1">
            Choose a fresh password for your account
          </p>
        </div>

        <div className="p-8">
          {!token ? (
            <div className="text-center space-y-4" data-testid="reset-no-token">
              <p className="font-nunito text-sm text-slate-600">
                This reset link is missing its token. Please request a new one.
              </p>
              <Link to="/" className="inline-block font-nunito text-sm text-[#FD5E53] hover:underline">
                Back to home
              </Link>
            </div>
          ) : success ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-14 h-14 text-[#16A34A] mx-auto" />
              <p className="font-fredoka text-lg text-slate-800">Password updated!</p>
              <p className="font-nunito text-sm text-slate-600">
                Redirecting you to sign in...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm font-nunito" data-testid="reset-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="new-password" className="font-nunito font-semibold text-slate-700 text-sm">
                  New password
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 pr-12 font-nunito"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="reset-toggle-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="font-nunito font-semibold text-slate-700 text-sm">
                  Confirm new password
                </Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat it"
                  required
                  minLength={8}
                  className="rounded-2xl border-2 border-slate-100 focus:border-[#38BDF8] py-3 px-4 font-nunito"
                  data-testid="reset-confirm-input"
                />
              </div>

              <motion.button
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#FD5E53] hover:bg-[#E84E44] text-white font-fredoka font-medium text-base py-3.5 rounded-full shadow-md transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="reset-submit-btn"
              >
                {isLoading ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      className="w-5 h-5 border-2 border-white border-t-transparent rounded-full inline-block"
                    />
                    Updating...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4" /> Update password
                  </>
                )}
              </motion.button>

              <div className="text-center">
                <Link to="/" className="font-nunito text-sm text-slate-500 hover:text-[#FD5E53] transition-colors" data-testid="reset-back-home">
                  Back to home
                </Link>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
